import React from "react";
import { useTranslation } from "react-i18next";
import {
  $Row,
  $Col,
  $Card,
  $Form,
  $Select,
  $Input,
  $AutoComplete,
  $Button,
} from "us.common/components";
import {
  DocRow,
  EntityTypeOptions,
} from "us.collection.input-output/constants";

type BulkActionPanelProps = {
  selectedCount: number;
  selectedKeys: string[];
  values: any;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  documentCategoryOptions: Array<{ value: string }>;
  entityInfo?: any;
  performBulkGetEntityData: (bulkAssignTo?: string) => void;
  handleBulkMap: () => void;
  updateRowsByKeys: (keys: string[], patch: Partial<DocRow>) => void;
};
/**
@description
BulkActionPanel Component
Provides UI for bulk actions on selected documents (assign, category, debtor/creditor fields).
Handles updating multiple rows, entity lookup for bulk assign, and upload constraints display.
Features:
- Bulk entity type selection
- Bulk assign to input and lookup
- Bulk category selection
- Constraints display for uploads
@link htKeshan Nimnaka <keshana@unicorn-solutions.com>
@since 29/06/2026
*/
const BulkActionPanel: React.FC<BulkActionPanelProps> = ({
  selectedCount,
  selectedKeys,
  values,
  setFieldValue,
  documentCategoryOptions,
  entityInfo,
  performBulkGetEntityData,
  handleBulkMap,
  updateRowsByKeys,
}) => {
  const { t } = useTranslation();

  return (
    <$Card bordered={true} className="bulk-action-section-card">
      <div className="bulk-action-body">
        <div className="home-section-title">
          {t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.BULK_ACTIONS")}
        </div>

        <$Form layout="vertical">
          <$Row gutter={16}>
            <$Col span={3}>
              <span>
                {t(
                  "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.ENTITY_TYPE",
                )}
              </span>
              <$Select
                name="bulkEntityType"
                size="small"
                allOption={false}
                className="bulk-ent-type-selection home-full-width"
                value={values.bulkEntityType ?? ""}
                disabled={selectedCount < 2}
                options={EntityTypeOptions}
                onChange={(value: any) => {
                  const entityType = String(value ?? "");
                  setFieldValue("bulkEntityType", entityType);
                  updateRowsByKeys(selectedKeys, {
                    entityType,
                    canConfirm: false,
                  });
                }}
              />
            </$Col>
            <$Col span={6}>
              <$Input
                name="bulkAssignTo"
                size="small"
                value={values.bulkAssignTo}
                disabled={selectedCount < 2}
                label={t(
                  "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER_HISTORY.ASSIGN_TO",
                )}
                onChange={(e: any) => {
                  const val = (e.target as HTMLInputElement).value;
                  setFieldValue("bulkAssignTo", val);
                  updateRowsByKeys(selectedKeys, {
                    assignTo: val,
                    canConfirm: false,
                    loading: true
                  });
                }}
                onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    (e.currentTarget as HTMLInputElement).blur();
                  }
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const assignTo = (e.target as HTMLInputElement).value;
                  if (!assignTo) return;
                  performBulkGetEntityData(assignTo);
                }}
              />
            </$Col>

            <$Col span={6}>
              <$AutoComplete
                size="small"
                name="bulkCategory"
                label={t("US.COLLECTION.COMMON:COMMON.CATEGORY")}
                disabled={selectedCount < 2}
                className="home-full-width"
                options={documentCategoryOptions}
                value={values.bulkCategory}
                onChange={(value: any) => {
                  const category = String(value ?? "");
                  setFieldValue("bulkCategory", category);
                  updateRowsByKeys(selectedKeys, {
                    category,
                  });
                }}
              />
            </$Col>
            <$Col span={3} className="home-assign-col">
              <$Button
                type="primary"
                className="home-assign-btn"
                name="bulkMapButton"
                style={{ padding: "0 14px" }}
                disabled={selectedCount < 2 || !entityInfo?.data?.entityId}
                onClick={() => handleBulkMap()}
              >
                {t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.ASSIGN")}
              </$Button>
            </$Col>
          </$Row>

          <$Row gutter={16} align="top">
            <$Col span={9}>
              <$Input
                key={`bulk-debtor-${values.bulkDebtorName}-${selectedCount}`}
                name="bulkDebtorName"
                size="small"
                value={values.bulkDebtorName}
                disabled={selectedCount < 2}
                label={t("US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.DEBTOR_NAME")}
              />
            </$Col>

            <$Col span={9}>
              <$Input
                key={`bulk-creditor-${values.bulkCreditorName}-${selectedCount}`}
                name="bulkCreditorName"
                size="small"
                value={values.bulkCreditorName}
                disabled={selectedCount < 2}
                label={t("US.COLLECTION.CREDITOR:CREDITOR_INFO.CREDITOR_NAME")}
                onChange={(e: any) =>
                  setFieldValue(
                    "bulkCreditorName",
                    (e.target as HTMLInputElement).value,
                  )
                }
              />
            </$Col>

            <$Col span={6}>
              <div className="constraint-col">
                <div className="vertical-divider"></div>

                <div className="upload-constraints">
                  <div className="upload-constraints-content">
                    <div className="upload-title">
                      {t(
                        "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.UPLOAD_CONSTRAINTS",
                      )}
                    </div>
                    <div>
                      {t(
                        "US.COLLECTION.INPUT-OUTPUT:DOCUMENTS_MANAGER.UPLOAD_CONSTRAINTS_VALUE",
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </$Col>
          </$Row>
        </$Form>
      </div>
    </$Card>
  );
};

export default BulkActionPanel;
